import * as Sentry from '@sentry/vue';
import { logoutUserMix } from '@/mixins';

export const init = (app, router) => {
  Sentry.init({
    app,
    environment: process.env.VUE_APP_STAGE,
    enabled: process.env.VUE_APP_STAGE === 'app',
    release: `finclass-webapp@${require('../../package.json').version}`,
    dsn: 'https://1327c15bc668456eafd62f239189fab8@o245296.ingest.us.sentry.io/4505284699619328',
    logErrors: true,
    attachProps: true,
    attachStacktrace: true,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
        routeLabel: 'path',
      }),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [
          globalThis.location.origin,
          'api-store.timeprimo.com',
          'hml-store.timeprimo.com',
          'https://app.vindi.com.br/api/v1/public/payment_profiles',
          'https://sandbox-app.vindi.com.br/api/v1/public/payment_profiles',
        ],
        networkRequestHeaders: ['X-Gprim-Specdev', 'X-Gprim-Specnav', 'X-Gprim-Key', 'X-Custom-Header'],
        networkResponseHeaders: ['X-Gprim-Specdev', 'X-Gprim-Specnav', 'X-Gprim-Key', 'X-Custom-Header'],
        maskAllText: false,
        maskAllInputs: true,
        beforeAddRecordingEvent: sanitizeBeforeSendEvent,
      }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    tracesSampleRate: 1,
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    ignoreErrors: [
      '/pagead/viewthroughconversion/406018914/',
      'AxiosError: Request failed with status code \\d{3}',
      'AxiosError: timeout exceeded',
      'AxiosError: Network Error',
      "Cannot read properties of null \\(reading 'removeAttribute'\\)",
      "Cannot read properties of undefined \\(reading 'removeAttribute'\\)",
      'TypeError: Cannot read properties of null',
      'TypeError: Cannot read properties of undefined',
      'ReferenceError:',
      'ChunkLoadError:',
      'Loading chunk',
      'Loading CSS chunk',
      'Loading CSS chunk.*failed',
      'chunk.*failed',
      'CSS chunk.*failed',
      'Script error',
      'Non-Error promise rejection',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Unhandled promise rejection',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'Connection failed',
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /pagead2\.googlesyndication\.com/i,
      /^safari:\/\//i,
      /^safari-extension:\/\//i,
      /^gtag\/\//i,
      /vendor\//i,
      /node_modules\//i,
      /\.min\.js$/i,
      /\.bundle\.js$/i,
      /chunk\..*\.js$/i,
      /chunk\..*\.css$/i,
      /app\..*\.js$/i,
      /app\..*\.css$/i,
      /videojs/i,
      /nuevo\.min\.js/i,
      /swiper/i,
      /chart\.js/i,
      /amcharts/i,
      /google-analytics/i,
      /gtag/i,
      /clarity/i,
      /posthog/i,
    ],
    beforeSend(event, hint) {
      validateQuotaExceededError(event);

      if (isVendorError(event) || hint?.originalException === 'Timeout') {
        return null;
      }

      sanitizeBeforeSend(event);
      return event;
    },
  });
};

const sanitizeBeforeSendEvent = (event) => {
  const body = event?.data?.payload?.data?.request?.body;
  if (body?.payment_method_code === 'credit_card') {
    const safeBody = { ...body };
    delete safeBody.card_cvv;
    delete safeBody.holder_name;
    delete safeBody.card_expiration;
    safeBody.card_number = safeBody.card_number?.slice(0, 6);
    event.data.payload.data.request.body = safeBody;
  }
  return event;
};

const sanitizeBeforeSend = (event) => {
  // Remove dados sensíveis de requisições
  const data = event.request?.data;
  if (data?.payment_method_code === 'credit_card') {
    const safeData = { ...data };
    delete safeData.card_cvv;
    delete safeData.holder_name;
    delete safeData.card_expiration;
    safeData.card_number = safeData.card_number?.slice(0, 6);
    event.request.data = safeData;
  }

  // Remove dados sensíveis de contexts (ex: Vindi)
  if (event.contexts?.vindi) {
    const safeCtx = { ...event.contexts.vindi };
    delete safeCtx.card_cvv;
    delete safeCtx.holder_name;
    delete safeCtx.card_expiration;
    safeCtx.card_number = safeCtx.card_number?.slice(0, 6);
    event.contexts.vindi = safeCtx;
  }
};

const isVendorError = (event) => {
  const stacktrace = event?.exception?.values?.[0]?.stacktrace;
  if (stacktrace?.frames) {
    return stacktrace.frames.some((frame) => {
      const filename = frame.filename || '';

      return (
        filename.includes('vendor/') ||
        filename.includes('node_modules/') ||
        filename.includes('.min.js') ||
        filename.includes('.bundle.js') ||
        filename.includes('chunk.') ||
        filename.includes('app.') ||
        filename.includes('videojs') ||
        filename.includes('nuevo.min.js') ||
        filename.includes('swiper') ||
        filename.includes('chart.js') ||
        filename.includes('amcharts') ||
        filename.includes('google-analytics') ||
        filename.includes('gtag') ||
        filename.includes('clarity') ||
        filename.includes('posthog') ||
        filename.includes('jquery') ||
        filename.includes('bootstrap') ||
        filename.includes('lodash') ||
        filename.includes('moment') ||
        filename.includes('axios') ||
        filename.includes('vue') ||
        filename.includes('vuex') ||
        filename.includes('vue-router')
      );
    });
  }

  const message = event?.exception?.values?.[0]?.value || '';
  const commonVendorErrors = [
    'removeAttribute',
    'Cannot read properties of null',
    'Cannot read properties of undefined',
    'TypeError: Cannot read properties',
    'ReferenceError:',
    'ChunkLoadError',
    'Loading chunk',
    'Loading CSS chunk',
    'chunk.*failed',
    'CSS chunk.*failed',
    'Script error',
    'Non-Error promise rejection',
  ];

  return commonVendorErrors.some((error) => message.includes(error));
};

const validateQuotaExceededError = (event) => {
  const values = event?.exception?.values || [];

  const isQuotaExceededError = values.some((value) => value.type === 'QuotaExceededError');

  if (isQuotaExceededError) {
    logoutUserMix.methods.logoutUser();
  }
};
